import React, { useCallback } from 'react';
import { useFormikContext } from 'formik';

import { useStore } from 'core/store/';
import useSession from 'core/hooks/use-session';
import { Discount } from '../discount';
import { track } from 'core/helpers/analytics';

const discountConfig = {
  auto_home_savings: {
    formField: 'global.multiPolicyDiscount',
    storeMethod: 'setIncludeMultiPolicyDiscount',
    trackingName: 'Auto/Home Bundle Discount'
  },
  legacy_savings: {
    formField: 'global.legacyDiscount',
    storeMethod: 'setIncludeLegacyDiscount',
    trackingName: 'Legacy Discount'
  },
  military_savings: {
    formField: 'global.militaryDiscount',
    storeMethod: 'setIncludeMilitaryDiscount',
    trackingName: 'Military Discount'
  },
  education_savings: {
    formField: 'global.educationDiscount',
    storeMethod: 'setIncludeEducationDiscount',
    trackingName: 'Education Discount'
  },
  crop_insurance_savings: {
    formField: 'global.cropInsuranceDiscount',
    storeMethod: 'setIncludeCropInsuranceDiscount',
    trackingName: 'Crop Insurance Discount'
  },
  traditions_savings: {
    formField: 'global.traditionsDiscount',
    storeMethod: 'setIncludeTraditionsDiscount',
    trackingName: 'Traditions Discount'
  },
  association_savings: {
    formField: 'global.farmAssociationDiscount',
    storeMethod: 'setIncludeAssociationDiscount',
    trackingName: 'Association Discount'
  },
  claim_free_savings: {
    formField: 'global.claimFreeDiscount',
    storeMethod: 'setIncludeClaimFreeDiscount',
    trackingName: 'Claim Free Discount'
  }
};

const FarmDiscount = ({ isActive, id, ...discount }) => {
  const { setFieldValue, values } = useFormikContext();
  const { offer: store } = useStore();
  const { canApproveUWReview } = useSession();

  const config = discountConfig[id];
  const fieldPath = config.formField.split('.');
  const isApplied = fieldPath.reduce((obj, key) => obj?.[key], values) ?? isActive;
  const state = store.offer?.quote?.correctedAddress?.state;

  const onAddClick = useCallback(() => {
    const addValue = true;
    setFieldValue(config.formField, addValue);
    store[config.storeMethod](addValue);
    track(`${config.trackingName} Added`, {
      discount: discount.label,
      offerId: store.offer?.id,
      state
    });
  }, [setFieldValue, store, config, discount.label, state]);

  const onRemoveClick = useCallback(() => {
    setFieldValue(config.formField, false);
    store[config.storeMethod](false);
    track(`${config.trackingName} Removed`, {
      discount: discount.label,
      offerId: store.offer?.id,
      state
    });
  }, [setFieldValue, store, config, discount.label, state]);

  const onResetClick = useCallback(() => {
    setFieldValue(config.formField, null);
    store[config.storeMethod](null);
    track(`${config.trackingName} Reset to Auto`, {
      discount: discount.label,
      offerId: store.offer?.id,
      state
    });
  }, [setFieldValue, store, config, discount.label, state]);

  let onAdd = onAddClick;
  let onRemove = onRemoveClick;
  let onReset;

  // claim_free_savings is UW-only: gate add/remove on canApproveUWReview and expose
  // a reset-to-auto action when an explicit override (true/false) is currently set.
  if (id === 'claim_free_savings') {
    const claimFreeOverride = fieldPath.reduce((obj, key) => obj?.[key], values);
    const hasOverride = claimFreeOverride !== null && claimFreeOverride !== undefined;
    onReset = canApproveUWReview && hasOverride ? onResetClick : undefined;
    if (!canApproveUWReview) {
      onAdd = undefined;
      onRemove = undefined;
    }
  }

  return <Discount {...discount} id={id} isActive={isApplied} onAdd={onAdd} onRemove={onRemove} onReset={onReset} />;
};

export default FarmDiscount;
